'use client';
import { useState } from 'react';

import { Locale } from '@core/Entities/Locale/Locale.entity';
import { getLabelWithEllipsis } from '@core/Utils/Strings/GetLabelWithEllipsis';
import Button from '@gds/Button/Button';
import { Collapse } from '@gds/Collapse/Collapse';
import { Typography } from '@gds/Typography/Typography';

import { PrefetchLink } from '@shared-components/PrefetchLink/PrefetchLink';

import { OptionalPageBuilderProps } from '../../../Entities/PageBuilder.entity';

import { InterlinkingContent } from '../Entities/InterLinking.entity';

import styles from './Collapsiblelnterlinking.module.css';
const LinkBlock = (
  linkBlocks: Omit<
    InterlinkingContent['interlinkingItems'][number]['linkBlocks'][number],
    'heading'
  > & { heading: string | null },
) => {
  return (
    <div className={styles.section}>
      {linkBlocks.heading ? (
        <Typography className={styles.sectionHeading} Component="h3" variant="subheading2">
          {linkBlocks.heading}
        </Typography>
      ) : null}
      <ol className={styles.ctas} aria-label={linkBlocks.heading ?? ''}>
        {linkBlocks.cta.map(cta => {
          return (
            <li key={cta.ctaText} aria-label={cta.ctaText}>
              <PrefetchLink href={cta.link ?? '/'} key={cta.ctaText}>
                {getLabelWithEllipsis(cta.ctaText, 30, 27)}
              </PrefetchLink>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export const CollapsibleInterlinking = (
  interlinking: Omit<OptionalPageBuilderProps<InterlinkingContent>, 'locale'> & { locale: Locale },
) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const messages = {
    uk: {
      viewMore: 'View more',
      viewLess: 'View less',
    },
    de: {
      viewMore: 'Mehr anzeigen',
      viewLess: 'Weniger sehen',
    },
    fr: {
      viewMore: 'Afficher tout',
      viewLess: 'Afficher moins',
    },
  };

  return (
    <>
      {interlinking.interlinkingItems.map((interlinkingItem, index) => (
        <div className={styles.container} key={`${interlinkingItem.heading}-${index}`}>
          <Typography className={styles.title} variant="h2">
            {interlinkingItem.heading}
          </Typography>

          <div className={styles.sections}>
            {interlinkingItem.linkBlocks?.slice(0, 6).map(linkBlock => {
              const limit = 6;
              const visibleOptions = linkBlock.cta.slice(0, limit);
              const hasMoreOptions = linkBlock.cta.length > limit;
              const hiddenCtas = hasMoreOptions ? linkBlock.cta.slice(visibleOptions.length) : [];
              return (
                <div
                  key={linkBlock.heading}
                  className={styles.collapsibleSection}
                  {...(linkBlock.dataTestId && { 'data-test-id': linkBlock.dataTestId })}
                >
                  <LinkBlock cta={visibleOptions} heading={linkBlock.heading} />
                  {hasMoreOptions && (
                    <>
                      <Collapse open={isExpanded}>
                        <div className={styles.collapsibleSectionContent}>
                          <LinkBlock
                            key={`${linkBlock.heading}-more`}
                            heading={null}
                            cta={hiddenCtas}
                          />
                        </div>
                      </Collapse>

                      <Button
                        variant="link"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className={styles.viewMoreButton}
                      >
                        {isExpanded
                          ? messages[interlinking.locale || 'uk'].viewLess
                          : messages[interlinking.locale || 'uk'].viewMore}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};
